import { Injectable, inject, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ResolverCacheService {
  private platformId = inject(PLATFORM_ID);
  private transferState = inject(TransferState);
  private clientCache = new Map<string, any>(); // eslint-disable-line

  /**
   * Creates a cached resolver function that handles server-side and client-side caching
   * @param cacheKey - Unique key for the cache
   * @param dataFetcher - Function that returns an Observable of the data to cache
   * @returns Observable of the cached or fresh data
   */
  createCachedResolver<T>(
    cacheKey: string,
    dataFetcher: () => Observable<T>
  ): Observable<T> {
    const STATE_KEY = makeStateKey<T>(cacheKey);

    if (this.clientCache.has(cacheKey)) {
      return of(this.clientCache.get(cacheKey));
    }

    if (this.platformId === 'server') {
      return dataFetcher().pipe(
        tap(data => {
          this.transferState.set(STATE_KEY, data);
          this.clientCache.set(cacheKey, data);
        })
      );
    }

    const cachedData = this.transferState.get(STATE_KEY, null);
    if (cachedData) {
      this.clientCache.set(cacheKey, cachedData);
      return of(cachedData);
    }

    return dataFetcher().pipe(
      tap(data => {
        this.clientCache.set(cacheKey, data);
      })
    );
  }

  /**
   * Creates a cached resolver function with route parameters
   * @param cacheKeyPrefix - Prefix for the cache key
   * @param routeParam - Route parameter to include in cache key
   * @param dataFetcher - Function that returns an Observable of the data to cache
   * @returns Observable of the cached or fresh data
   */
  createCachedResolverWithParam<T>(
    cacheKeyPrefix: string,
    routeParam: string | null,
    dataFetcher: () => Observable<T>
  ): Observable<T> {
    if (!routeParam) {
      return of(null as T);
    }

    const cacheKey = `${cacheKeyPrefix}-${routeParam}`;
    return this.createCachedResolver(cacheKey, dataFetcher);
  }
}
